import { Person, type DeepReadonly } from '@tadpoles/common'
import { Type, type Static } from 'typebox'
import { Value } from 'typebox/value'

import type {
    MappedPersistenceCollectionDefinition,
    PersistenceCollection
} from '@tadpoles/persistence'

type UserContextStorageRecord = Static<typeof UserContextStorageRecord>
const UserContextStorageRecord = Type.Object({
    id: Type.Literal('current'),
    namespace: Type.String({ minLength: 1 }),
    cachedAt: Type.Integer({ minimum: 0 }),
    person: Person
})
const CurrentUserContextId = 'current'

export type UserContextRecord = {
    cachedAt: number
} & Omit<UserContextStorageRecord, 'id' | 'cachedAt'>
export type UserContextRecordInput = DeepReadonly<Omit<UserContextStorageRecord, 'id'>>
type UserContextCollection = PersistenceCollection<UserContextRecord, 'id'>

export interface FamilyUserContextStore {
    clear(): Promise<void>
    get(): Promise<UserContextRecord | null>
    put(value: UserContextRecord): Promise<void>
}

export function decodeUserContextRecord(value: UserContextRecordInput): UserContextRecord {
    const decodedRecord = Value.Decode(UserContextStorageRecord, {
        ...value,
        id: 'current'
    })
    const { id, ...record } = Value.Encode(UserContextStorageRecord, decodedRecord)
    void id

    return record
}

export const UserContextCollectionDefinition: MappedPersistenceCollectionDefinition<
    typeof UserContextStorageRecord,
    UserContextRecord,
    'id'
> = {
    kind: 'user-context',
    scope: 'global',
    primaryKey: 'id',
    modelSchema: UserContextStorageRecord,
    encode: (value) =>
        Value.Decode(UserContextStorageRecord, {
            id: 'current',
            ...value
        }),
    decode: (value) => {
        const { id, ...record } = Value.Encode(UserContextStorageRecord, value)
        void id

        return record
    }
}

export class FamilyUserContextStoreAdapter implements FamilyUserContextStore {
    readonly #collection: UserContextCollection

    public constructor(collection: UserContextCollection) {
        this.#collection = collection
    }

    public async clear(): Promise<void> {
        await this.#collection.remove(CurrentUserContextId)
    }

    public async get(): Promise<UserContextRecord | null> {
        return await this.#collection.get(CurrentUserContextId)
    }

    public async put(value: UserContextRecord): Promise<void> {
        await this.#collection.put(value)
    }
}
